import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import api from './api';
import { getBracketFormatLabel } from './utils/championshipFormat';
import './styles/index.css';
import Logo26 from '../../assets/Logo26.png';

// Constantes del bracket (deben coincidir con CSS)
const CARD_HEIGHT = 90;   // height de .bracket-match-card
const CARD_WIDTH = 180;   // width de .bracket-match-card
const HORIZONTAL_GAP = 50; // espacio para conectores
const VERTICAL_GAP = 16;
const CONNECTOR_LINE_LENGTH = 25; // mitad del gap horizontal
const LINE_THICKNESS = 2;
const TITULO_ALTURA = 45;

const BracketAdmin = ({ bracketData, campeonatoId, campeonatoNombre, onUpdate }) => {
  const [modoVista, setModoVista] = useState('winner');
  const [selectedPartida, setSelectedPartida] = useState(null);
  const [modalType, setModalType] = useState(null); // 'menu', 'editar', 'asignar', 'cambiar1', 'cambiar2', 'configuracion'
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [jugadoresDisponibles, setJugadoresDisponibles] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [showCampeonModal, setShowCampeonModal] = useState(false);
  const scrollContainerRef = useRef(null);
  
  // Estados para formularios de modales
  const [formEditar, setFormEditar] = useState({ sets1: 0, sets2: 0, ganadorId: '', forzar: false });
  const [formResultado, setFormResultado] = useState({ sets1: 4, sets2: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [confirmResetear, setConfirmResetear] = useState(false);
  
  // Estado para buscador de jugadores del torneo
  const [buscarJugador, setBuscarJugador] = useState('');
  const [showBuscador, setShowBuscador] = useState(false);
  const [jugadorResaltado, setJugadorResaltado] = useState(null);
  const partidaRefs = useRef({});

  // Cargar jugadores disponibles
  useEffect(() => {
    loadJugadoresDisponibles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campeonatoId]);

  // Buscar usuarios en la base de datos con debounce
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const data = await api.searchUsers(searchTerm);
        setSearchResults(data || []);
      } catch (err) {
        console.error('Error buscando usuarios:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Actualizar formulario cuando cambia la partida seleccionada
  useEffect(() => {
    if (selectedPartida) {
      setFormEditar({
        sets1: selectedPartida.resultado?.jugador1Sets || 0,
        sets2: selectedPartida.resultado?.jugador2Sets || 0,
        ganadorId: selectedPartida.ganador?._id || selectedPartida.ganador || '',
        forzar: false,
      });
      setSearchTerm('');
    }
  }, [selectedPartida]);

  const loadJugadoresDisponibles = async () => {
    try {
      const data = await api.getJugadoresDisponibles(campeonatoId);
      setJugadoresDisponibles(data.disponibles || []);
      setParticipantes(data.participantes || []);
    } catch (error) {
      console.error('Error cargando jugadores:', error);
    }
  };

  // Mostrar mensaje temporal
  const showMensaje = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: '', texto: '' }), 4000);
  };

  // Buscar jugador y hacer scroll a su posición
  const buscarYScrollAJugador = (jugadorId, jugadorNombre) => {
    if (!bracketData?.bracket) return;
    
    // Buscar en qué partida está el jugador
    const buscarEnBracket = (bracket, tipo) => {
      if (!bracket?.rondas) return null;
      
      for (const [rondaNombre, partidas] of Object.entries(bracket.rondas)) {
        for (const partida of partidas) {
          if (!partida) continue;
          
          const j1Id = partida.jugador1?._id || partida.jugador1;
          const j2Id = partida.jugador2?._id || partida.jugador2;
          
          if (String(j1Id) === String(jugadorId) || String(j2Id) === String(jugadorId)) {
            return { partida, rondaNombre, bracketTipo: tipo };
          }
        }
      }
      return null;
    };
    
    // Buscar primero en winner, luego en loser
    let resultado = buscarEnBracket(bracketData.bracket.winner, 'winner');
    if (!resultado) {
      resultado = buscarEnBracket(bracketData.bracket.loser, 'loser');
    }
    
    if (resultado) {
      // Cambiar a la vista correcta si es necesario
      if (resultado.bracketTipo !== modoVista) {
        setModoVista(resultado.bracketTipo);
      }
      
      // Resaltar el jugador
      setJugadorResaltado(jugadorId);
      
      // Scroll a la partida después de un pequeño delay para que se renderice
      setTimeout(() => {
        const partidaRef = partidaRefs.current[resultado.partida._id];
        if (partidaRef) {
          partidaRef.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        }
      }, 100);
      
      // Quitar resaltado después de 3 segundos
      setTimeout(() => setJugadorResaltado(null), 5000);
      
      showMensaje('success', `📍 ${jugadorNombre} encontrado en ${resultado.rondaNombre}`);
    } else {
      showMensaje('error', `❌ ${jugadorNombre} no está en ninguna partida activa`);
    }
    
    setShowBuscador(false);
    setBuscarJugador('');
  };
  
  // Filtrar jugadores del torneo para el buscador (mínimo 2 caracteres)
  const jugadoresFiltrados = useMemo(() => {
    if (!buscarJugador || buscarJugador.length < 2) return [];
    
    return participantes.filter(p => {
      const nombre = (p.nombreUsuario || p.username || '').toLowerCase();
      return nombre.includes(buscarJugador.toLowerCase());
    });
  }, [participantes, buscarJugador]);

  // Determinar si es eliminación simple (debe estar antes del useMemo de rondas)
  const esEliminacionSimple = useMemo(() => {
    return bracketData?.info?.tipoEliminacion === 'simple' ||
           (!bracketData?.bracket?.loser?.rondas || Object.keys(bracketData?.bracket?.loser?.rondas || {}).length === 0);
  }, [bracketData?.info?.tipoEliminacion, bracketData?.bracket?.loser?.rondas]);

  // Obtener y ordenar rondas según modo
  const rondas = useMemo(() => {
    if (!bracketData?.bracket) return [];
    
    const bracket = modoVista === 'winner' 
      ? bracketData.bracket.winner 
      : bracketData.bracket.loser;
    
    if (!bracket?.rondas) return [];
    
    let rondasArray = Object.entries(bracket.rondas)
      .map(([nombre, partidas]) => ({
        nombre,
        partidas: (partidas || []).filter(p => p && p._id)
      }))
      .filter(r => r.partidas.length > 0);
    
    // En eliminación simple, filtrar la Gran Final (no existe, la Final Winner ES la final)
    if (esEliminacionSimple && modoVista === 'winner') {
      rondasArray = rondasArray.filter(r => r.nombre !== 'Gran Final');
    }
    
    return rondasArray.sort((a, b) => {
        const getOrden = (nombre) => {
          if (nombre === 'Gran Final') return 1000;
          if (nombre.includes('Final') && !nombre.includes('Gran')) return 999;
          const matchRonda = nombre.match(/Ronda (\d+)/);
          if (matchRonda) return parseInt(matchRonda[1]);
          if (nombre === 'Loser A') return 1;
          if (nombre === '+ Loser B') return 2;
          if (nombre === 'Loser R3') return 3;
          if (nombre === '+ Loser C') return 4;
          if (nombre === 'Loser R5') return 5;
          if (nombre === '+ Loser D') return 6;
          if (nombre === 'Loser R7') return 7;
          if (nombre === '+ Loser E') return 8;
          if (nombre === 'Loser R9') return 9;
          return 0;
        };
        return getOrden(a.nombre) - getOrden(b.nombre);
      });
  }, [bracketData, modoVista, esEliminacionSimple]);

  // Calcular multiplicador de espaciado
  const calcularMultiplicador = useCallback((rondaIdx, numPartidas, nombreRonda) => {
    const esLoserBracket = modoVista === 'loser';
    
    if (nombreRonda === 'Gran Final' && rondas[rondaIdx - 1]?.partidas?.length === 1) {
      return Math.pow(2, rondaIdx - 1);
    }
    
    if (!esLoserBracket) {
      return Math.pow(2, rondaIdx);
    }
    
    const primeraRondaPartidas = rondas[0]?.partidas?.length || 16;
    const ratio = primeraRondaPartidas / numPartidas;
    return Math.max(1, ratio);
  }, [modoVista, rondas]);

  // =====================================================
  // FUNCIÓN PARA EXTRAER EL NÚMERO DE PARTIDA DEL LOSER DESDE destinoPerdedor
  // El campo viene del backend con formato: "L-R1-3-P1", "L-R2-1-P2", "L-Final-P2", etc.
  // =====================================================
  const extraerNumeroLoser = useCallback((destinoPerdedor, rondaNombre) => {
    if (!destinoPerdedor || destinoPerdedor === 'ELIMINADO') {
      return null;
    }
    
    // Gran Final no tiene destino loser
    if (rondaNombre?.includes('Gran')) {
      return null;
    }
    
    // Si es L-Final, mostrar "LF"
    if (destinoPerdedor.includes('L-Final')) {
      return 'LF';
    }
    
    // Extraer ronda y partida del formato "L-R1-3-P1" → ronda=1, partidaNum=3
    const match = destinoPerdedor.match(/L-R(\d+)-(\d+)/);
    if (!match) return null;
    
    const rondaLoser = parseInt(match[1]);
    const partidaNumEnRonda = parseInt(match[2]);
    
    // Mapeo de L-Rx a nombre de ronda en frontend
    const nombreRondaLoser = rondaLoser === 1 ? 'Loser A' 
      : rondaLoser % 2 === 0 ? `+ Loser ${'ABCDEFGH'[rondaLoser / 2]}`
      : `Loser R${rondaLoser}`;
    
    // Buscar la posición real de esa partida en el Loser bracket
    if (bracketData?.bracket?.loser?.rondas) {
      const rondasLoser = bracketData.bracket.loser.rondas;
      const partidas = rondasLoser[nombreRondaLoser];
      
      if (partidas && Array.isArray(partidas)) {
        const partidasOrdenadas = [...partidas]
          .filter(p => p && p._id)
          .sort((a, b) => (a.posicion || 0) - (b.posicion || 0));
        
        if (partidaNumEnRonda <= partidasOrdenadas.length) {
          const partidaDestino = partidasOrdenadas[partidaNumEnRonda - 1];
          if (partidaDestino && partidaDestino.posicion) {
            return `L${partidaDestino.posicion}`;
          }
        }
      }
    }
    
    // Fallback: mostrar el formato simplificado
    return `L-R${rondaLoser}-${partidaNumEnRonda}`;
  }, [bracketData]);

  // Abrir modal
  const abrirModal = (partida, tipo) => {
    setSelectedPartida(partida);
    setModalType(tipo);
  };

  // Cerrar modal
  const cerrarModal = () => {
    setSelectedPartida(null);
    setModalType(null);
  };

  // ========== ACCIONES DE ADMINISTRACIÓN ==========

  // Editar resultado
  const handleEditarResultado = async (formData) => {
    if (!selectedPartida) return;
    
    setLoading(true);
    try {
      await api.editarResultadoPartida(campeonatoId, selectedPartida._id, {
        ganadorId: formData.ganadorId,
        resultado: {
          jugador1Sets: parseInt(formData.sets1) || 0,
          jugador2Sets: parseInt(formData.sets2) || 0,
        },
        forzar: formData.forzar,
      });
      
      showMensaje('success', '✅ Resultado actualizado correctamente');
      cerrarModal();
      onUpdate && onUpdate();
    } catch (error) {
      showMensaje('error', `❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Confirmar reseteo de partida
  const confirmarReseteo = async () => {
    if (!selectedPartida) return;
    
    setLoading(true);
    try {
      await api.resetearPartidaCampeonato(campeonatoId, selectedPartida._id);
      showMensaje('success', '✅ Partida reseteada correctamente');
      setConfirmResetear(false);
      cerrarModal();
      onUpdate && onUpdate();
    } catch (error) {
      showMensaje('error', `❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Registrar resultado con marcador personalizado
  const handleRegistrarResultado = async () => {
    if (!selectedPartida) return;
    
    const sets1 = parseInt(formResultado.sets1) || 0;
    const sets2 = parseInt(formResultado.sets2) || 0;
    
    // Validar que haya un ganador claro
    if (sets1 === sets2) {
      showMensaje('error', '❌ No puede haber empate. Uno debe ganar.');
      return;
    }
    
    // Determinar ganador por el marcador
    const j1Id = selectedPartida.jugador1?._id;
    const j2Id = selectedPartida.jugador2?._id;
    const ganadorId = sets1 > sets2 ? j1Id : j2Id;
    
    if (!ganadorId) {
      showMensaje('error', '❌ No se puede determinar el ganador');
      return;
    }
    
    setLoading(true);
    try {
      await api.registrarResultadoPartida(campeonatoId, selectedPartida._id, ganadorId, sets1, sets2);
      showMensaje('success', `✅ Resultado ${sets1}-${sets2} registrado. Jugador avanza de ronda.`);
      cerrarModal();
      onUpdate && onUpdate();
    } catch (error) {
      showMensaje('error', `❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Cambiar jugador
  const handleCambiarJugador = async (posicion, nuevoJugadorId) => {
    if (!selectedPartida || !nuevoJugadorId) return;
    
    setLoading(true);
    try {
      await api.cambiarJugadorPartida(campeonatoId, selectedPartida._id, {
        posicion,
        nuevoJugadorId,
        esBye: false,
      });
      showMensaje('success', '✅ Jugador cambiado');
      cerrarModal();
      loadJugadoresDisponibles();
      onUpdate && onUpdate();
    } catch (error) {
      showMensaje('error', `❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Poner BYE en una posición
  const handlePonerBye = async (posicion) => {
    if (!selectedPartida) return;
    
    setLoading(true);
    try {
      await api.cambiarJugadorPartida(campeonatoId, selectedPartida._id, {
        posicion,
        nuevoJugadorId: null,
        esBye: true,
      });
      showMensaje('success', '✅ BYE asignado - el otro jugador avanza automáticamente');
      cerrarModal();
      loadJugadoresDisponibles();
      onUpdate && onUpdate();
    } catch (error) {
      showMensaje('error', `❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Tarjeta de partida con acciones de admin
  const PartidaCard = ({ partida, resaltarJugador, partidaIndex, rondaIndex, rondaNombre }) => {
    if (!partida || !partida._id) return null;
    
    const esLoserBracket = modoVista === 'loser';
    
    // Extraer número de partida del Loser desde destinoPerdedor (solo Winner Bracket)
    const numeroLoserDestino = !esLoserBracket ? extraerNumeroLoser(partida.destinoPerdedor, rondaNombre) : null;
    
    const j1Nombre = partida.nombreJugador1 || partida.jugador1?.username || 'TBD';
    const j2Nombre = partida.nombreJugador2 || partida.jugador2?.username || 'TBD';
    
    const ganadorId = typeof partida.ganador === 'string' 
      ? partida.ganador 
      : partida.ganador?._id;
    
    const j1Id = partida.jugador1?._id || partida.jugador1;
    const j2Id = partida.jugador2?._id || partida.jugador2;
    
    const j1Ganador = ganadorId && String(ganadorId) === String(j1Id);
    const j2Ganador = ganadorId && String(ganadorId) === String(j2Id);
    
    // Verificar si algún jugador está resaltado
    const j1Resaltado = resaltarJugador && String(resaltarJugador) === String(j1Id);
    const j2Resaltado = resaltarJugador && String(resaltarJugador) === String(j2Id);
    const cardResaltada = j1Resaltado || j2Resaltado;
    
    const esBye = partida.isBye;
    const estaCompletada = partida.completada;
    const stats = partida.estadisticasPartida;
    const tieneStats = stats && (
      (stats.jugador1?.average || 0) > 0 || (stats.jugador2?.average || 0) > 0 ||
      (stats.jugador1?.count180s || 0) > 0 || (stats.jugador2?.count180s || 0) > 0
    );
    
    const score1 = partida.resultado?.jugador1Sets ?? '-';
    const score2 = partida.resultado?.jugador2Sets ?? '-';

    return (
      <div 
        className={`bracket-match-card admin-card
          ${estaCompletada ? 'completada' : ''} 
          ${esBye ? 'bye' : ''}
          ${cardResaltada ? 'resaltada' : ''}`}
        onClick={() => abrirModal(partida, 'menu')}
      >
        {/* Header con número de partida y destino */}
        <div className="match-header">
          <div className="match-header-left">
            <span className="match-position">#{partida.posicion ?? 0}</span>
            {/* Número de partida del Loser destino - SOLO en Winner Bracket */}
            {numeroLoserDestino && (
              <span className="destino-loser-badge" title={`Perdedor va a partida ${numeroLoserDestino}`}>
                → {numeroLoserDestino}
              </span>
            )}
          </div>
          <div className="match-badges">
            {estaCompletada && !esBye && <span className="badge-completada">✓</span>}
            {esBye && <span className="badge-bye">BYE</span>}
            {tieneStats && <span className="badge-stats">📊</span>}
            <span className="badge-admin">⚙️</span>
          </div>
        </div>
        
        {/* Jugador 1 */}
        <div className={`match-player 
          ${j1Ganador ? 'ganador' : ''} 
          ${estaCompletada && !j1Ganador && !esBye ? 'perdedor' : ''}
          ${j1Resaltado ? 'resaltado' : ''}`}
        >
          <span className={`player-name ${j1Nombre === 'TBD' ? 'tbd' : ''}`}>
            {j1Resaltado && '📍 '}{j1Nombre}
          </span>
          <span className={`player-score ${j1Ganador ? 'ganador' : ''}`}>
            {estaCompletada ? score1 : '-'}
          </span>
        </div>
        
        <div className="match-divider"></div>
        
        {/* Jugador 2 */}
        <div className={`match-player 
          ${j2Ganador ? 'ganador' : ''} 
          ${estaCompletada && !j2Ganador && !esBye ? 'perdedor' : ''}
          ${j2Resaltado ? 'resaltado' : ''}`}
        >
          <span className={`player-name ${j2Nombre === 'TBD' ? 'tbd' : ''}`}>
            {j2Resaltado && '📍 '}{j2Nombre}
          </span>
          <span className={`player-score ${j2Ganador ? 'ganador' : ''}`}>
            {estaCompletada ? score2 : '-'}
          </span>
        </div>
        
        {/* Indicador clickeable */}
        <div className="admin-click-hint">Click para administrar</div>
      </div>
    );
  };

  // Calcular altura
  const calcularAlturaBracket = useMemo(() => {
    if (rondas.length === 0) return 300;
    const primeraRonda = rondas[0];
    const numPartidas = primeraRonda?.partidas?.length || 1;
    const espaciadoBase = CARD_HEIGHT + VERTICAL_GAP;
    return TITULO_ALTURA + (numPartidas * espaciadoBase) + 50;
  }, [rondas]);

  // Renderizar bracket
  const renderBracket = () => {
    if (rondas.length === 0) {
      return (
        <div className="bracket-empty">
          <p>{modoVista === 'loser' 
            ? 'El Loser Bracket aún no tiene partidas' 
            : 'No hay partidas disponibles'}
          </p>
        </div>
      );
    }

    const espaciadoBase = CARD_HEIGHT + VERTICAL_GAP;

    return (
      <div className="bracket-content-wrapper">
        {/* Patrón de logos como fondo */}
        <div className="bracket-logo-pattern">
          {Array.from({ length: 30 }).map((_, i) => (
            <img 
              key={`logo-${i}`}
              src={Logo26}
              alt=""
              className="bracket-logo-item"
            />
          ))}
        </div>
        
        {rondas.map((ronda, rondaIndex) => {
          const numPartidas = ronda.partidas.length;
          const siguienteRonda = rondas[rondaIndex + 1];
          const siguienteNumPartidas = siguienteRonda?.partidas?.length || null;
          
          const multiplicador = calcularMultiplicador(rondaIndex, numPartidas, ronda.nombre);
          const espaciadoRonda = espaciadoBase * multiplicador;
          const offsetInicial = (espaciadoRonda - CARD_HEIGHT) / 2;
          const tieneConectores = rondaIndex < rondas.length - 1;
          const siguienteReduce = siguienteNumPartidas !== null && siguienteNumPartidas < numPartidas;
          
          return (
            <div key={`ronda-${rondaIndex}`} className="bracket-ronda-wrapper">
              <div className="bracket-ronda-column" style={{ width: CARD_WIDTH }}>
                <div className="bracket-ronda-header" style={{ height: TITULO_ALTURA }}>
                  <span className="ronda-titulo">{ronda.nombre}</span>
                </div>
                
                <div className="bracket-partidas-container">
                  {ronda.partidas.map((partida, idx) => {
                    if (!partida) return null;
                    const marginTop = idx === 0 ? offsetInicial : espaciadoRonda - CARD_HEIGHT;
                    
                    return (
                      <div 
                        key={partida._id || `p-${idx}`} 
                        className="bracket-partida-slot"
                        style={{ marginTop }}
                        ref={el => partidaRefs.current[partida._id] = el}
                      >
                        <PartidaCard 
                          partida={partida} 
                          resaltarJugador={jugadorResaltado}
                          partidaIndex={idx}
                          rondaIndex={rondaIndex}
                          rondaNombre={ronda.nombre}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {tieneConectores && (
                <div className="bracket-conectores-column" style={{ width: HORIZONTAL_GAP }}>
                  <svg 
                    className="conectores-svg"
                    style={{ 
                      width: HORIZONTAL_GAP, 
                      height: calcularAlturaBracket,
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}
                  >
                    {ronda.partidas.map((partida, idx) => {
                      if (!partida) return null;
                      
                      const posCard = TITULO_ALTURA + offsetInicial + (idx * espaciadoRonda);
                      const centroCard = posCard + (CARD_HEIGHT / 2);
                      const colorLinea = partida?.completada ? '#00C6CF' : '#3A4556';
                      
                      if (!siguienteReduce) {
                        return (
                          <line
                            key={`conn-${idx}`}
                            x1="0"
                            y1={centroCard}
                            x2={HORIZONTAL_GAP}
                            y2={centroCard}
                            stroke={colorLinea}
                            strokeWidth={LINE_THICKNESS}
                          />
                        );
                      }
                      
                      if (idx % 2 !== 0) return null;
                      
                      const partida2 = ronda.partidas[idx + 1];
                      const tienePareja = partida2 != null;
                      
                      const posCard2 = TITULO_ALTURA + offsetInicial + ((idx + 1) * espaciadoRonda);
                      const centroCard2 = tienePareja ? posCard2 + (CARD_HEIGHT / 2) : centroCard;
                      const puntoMedio = (centroCard + centroCard2) / 2;
                      
                      const p1Completada = partida?.completada;
                      const p2Completada = partida2?.completada;
                      const colorActivo = '#00C6CF';
                      const colorInactivo = '#3A4556';
                      
                      return (
                        <g key={`conn-${idx}`}>
                          <line x1="0" y1={centroCard} x2={CONNECTOR_LINE_LENGTH} y2={centroCard}
                            stroke={p1Completada ? colorActivo : colorInactivo} strokeWidth={LINE_THICKNESS} />
                          
                          {tienePareja && (
                            <>
                              <line x1="0" y1={centroCard2} x2={CONNECTOR_LINE_LENGTH} y2={centroCard2}
                                stroke={p2Completada ? colorActivo : colorInactivo} strokeWidth={LINE_THICKNESS} />
                              <line x1={CONNECTOR_LINE_LENGTH} y1={centroCard} x2={CONNECTOR_LINE_LENGTH} y2={centroCard2}
                                stroke={(p1Completada && p2Completada) ? colorActivo : colorInactivo} strokeWidth={LINE_THICKNESS} />
                            </>
                          )}
                          
                          <line x1={CONNECTOR_LINE_LENGTH} y1={puntoMedio} x2={HORIZONTAL_GAP} y2={puntoMedio}
                            stroke={(p1Completada && (p2Completada || !tienePareja)) ? colorActivo : colorInactivo}
                            strokeWidth={LINE_THICKNESS} />
                        </g>
                      );
                    })}
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Modal de menú de acciones
  const renderModalMenu = () => {
    if (!selectedPartida || modalType !== 'menu') return null;
    
    const j1Nombre = selectedPartida.nombreJugador1 || selectedPartida.jugador1?.username || 'TBD';
    const j2Nombre = selectedPartida.nombreJugador2 || selectedPartida.jugador2?.username || 'TBD';
    const estaCompletada = selectedPartida.completada;
    const tieneAmbosJugadores = selectedPartida.jugador1 && selectedPartida.jugador2;
    const esBye = selectedPartida.isBye;
    
    // Estadísticas de la partida - vienen de la partida misma
    const stats = selectedPartida.estadisticasPartida || selectedPartida.estadisticas || selectedPartida.stats;
    const statsJ1 = stats?.jugador1 || {};
    const statsJ2 = stats?.jugador2 || {};
    const tieneStats = !!stats && (
      Number.isFinite(statsJ1.average) || Number.isFinite(statsJ2.average) ||
      Number.isFinite(statsJ1.count180s) || Number.isFinite(statsJ2.count180s)
    );
    const ganadorId = typeof selectedPartida.ganador === 'string' 
      ? selectedPartida.ganador 
      : selectedPartida.ganador?._id;
    const j1Ganador = ganadorId && String(ganadorId) === String(selectedPartida.jugador1?._id);
    
    return (
      <div className="admin-modal-overlay" onClick={cerrarModal}>
        <div className="admin-modal" onClick={e => e.stopPropagation()}>
          <div className="admin-modal-header">
            <h3>⚙️ Administrar Partida #{selectedPartida.posicion}</h3>
            <button className="modal-close" onClick={cerrarModal}>✕</button>
          </div>
          
          <div className="admin-modal-info">
            <div className="match-preview">
              <span className="preview-player">{j1Nombre}</span>
              <span className="preview-vs">vs</span>
              <span className="preview-player">{j2Nombre}</span>
            </div>
            <span className={`status-badge ${estaCompletada ? 'completed' : 'pending'}`}>
              {estaCompletada ? '✓ Completada' : esBye ? '⏭️ BYE' : '⏳ Pendiente'}
            </span>
          </div>
          
          {/* Mostrar estadísticas si la partida está completada */}
          {estaCompletada && !esBye && (
            <div className="admin-stats-section">
              <div className="admin-stats-header">📊 Estadísticas del Encuentro</div>
              <div className="admin-stats-result">
                {selectedPartida.resultado?.jugador1Sets} - {selectedPartida.resultado?.jugador2Sets}
              </div>
              <div className="admin-stats-comparison">
                <div className={`admin-stats-player ${j1Ganador ? 'winner' : ''}`}>
                  <div className="admin-stats-name">{j1Ganador && '🏆 '}{j1Nombre}</div>
                  {tieneStats ? (
                    <>
                      <div className="admin-stat-row">
                        <span>Average:</span>
                        <strong>{(statsJ1.average || 0).toFixed(2)}</strong>
                      </div>
                      <div className="admin-stat-row">
                        <span>180s:</span>
                        <strong className="gold">{statsJ1.count180s || 0}</strong>
                      </div>
                    </>
                  ) : (
                    <div className="admin-stat-row no-stats">Sin estadísticas</div>
                  )}
                </div>
                <div className={`admin-stats-player ${!j1Ganador ? 'winner' : ''}`}>
                  <div className="admin-stats-name">{!j1Ganador && '🏆 '}{j2Nombre}</div>
                  {tieneStats ? (
                    <>
                      <div className="admin-stat-row">
                        <span>Average:</span>
                        <strong>{(statsJ2.average || 0).toFixed(2)}</strong>
                      </div>
                      <div className="admin-stat-row">
                        <span>180s:</span>
                        <strong className="gold">{statsJ2.count180s || 0}</strong>
                      </div>
                    </>
                  ) : (
                    <div className="admin-stat-row no-stats">Sin estadísticas</div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="admin-modal-actions">
            {/* Registrar resultado manual (partida pendiente con ambos jugadores) */}
            {!estaCompletada && tieneAmbosJugadores && !esBye && (
              <button 
                className="admin-action-btn primary"
                onClick={() => {
                  setFormResultado({ sets1: 4, sets2: 0 });
                  setModalType('resultado');
                }}
              >
                🎯 Registrar Resultado Manual
              </button>
            )}
            
            {/* Editar resultado (partida completada) */}
            {estaCompletada && !esBye && (
              <button 
                className="admin-action-btn edit"
                onClick={() => setModalType('editar')}
              >
                ✏️ Editar Resultado
              </button>
            )}
            
            {/* Resetear partida */}
            {estaCompletada && (
              <button 
                className="admin-action-btn reset"
                onClick={() => setConfirmResetear(true)}
                disabled={loading}
              >
                🔄 Resetear Partida
              </button>
            )}

            {/* Modal de confirmación de reseteo */}
            {confirmResetear && (
              <div className="confirm-overlay" onClick={() => setConfirmResetear(false)}>
                <div className="confirm-modal" onClick={e => e.stopPropagation()}>
                  <div className="confirm-icon">⚠️</div>
                  <h4>¿Resetear esta partida?</h4>
                  <p>Se perderá el resultado y el jugador que avanzó volverá a esta posición.</p>
                  <div className="confirm-buttons">
                    <button 
                      className="btn-cancel" 
                      onClick={() => setConfirmResetear(false)}
                      disabled={loading}
                    >
                      Cancelar
                    </button>
                    <button 
                      className="btn-confirm-danger" 
                      onClick={confirmarReseteo}
                      disabled={loading}
                    >
                      {loading ? 'Reseteando...' : 'Sí, resetear'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Cambiar jugador 1 por otro o BYE */}
            <button 
              className="admin-action-btn change"
              onClick={() => setModalType('cambiar1')}
            >
              👤 Cambiar {j1Nombre !== 'TBD' ? j1Nombre : 'Jugador 1'}
            </button>
            
            {/* Cambiar jugador 2 por otro o BYE */}
            <button 
              className="admin-action-btn change"
              onClick={() => setModalType('cambiar2')}
            >
              👤 Cambiar {j2Nombre !== 'TBD' ? j2Nombre : 'Jugador 2'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal de editar resultado
  const renderModalEditar = () => {
    if (!selectedPartida || modalType !== 'editar') return null;
    
    const j1Id = selectedPartida.jugador1?._id;
    const j2Id = selectedPartida.jugador2?._id;
    const j1Nombre = selectedPartida.nombreJugador1 || selectedPartida.jugador1?.username || 'Jugador 1';
    const j2Nombre = selectedPartida.nombreJugador2 || selectedPartida.jugador2?.username || 'Jugador 2';
    
    return (
      <div className="admin-modal-overlay" onClick={cerrarModal}>
        <div className="admin-modal" onClick={e => e.stopPropagation()}>
          <div className="admin-modal-header">
            <h3>✏️ Editar Resultado</h3>
            <button className="modal-close" onClick={cerrarModal}>✕</button>
          </div>
          
          <div className="admin-form">
            <div className="form-row">
              <label>{j1Nombre}</label>
              <input 
                type="number" 
                min="0" 
                max="7"
                value={formEditar.sets1}
                onChange={e => setFormEditar({...formEditar, sets1: e.target.value})}
              />
            </div>
            
            <div className="form-row">
              <label>{j2Nombre}</label>
              <input 
                type="number" 
                min="0" 
                max="7"
                value={formEditar.sets2}
                onChange={e => setFormEditar({...formEditar, sets2: e.target.value})}
              />
            </div>
            
            <div className="form-row">
              <label>Ganador</label>
              <select 
                value={formEditar.ganadorId}
                onChange={e => setFormEditar({...formEditar, ganadorId: e.target.value})}
              >
                <option value="">Seleccionar...</option>
                <option value={j1Id}>{j1Nombre}</option>
                <option value={j2Id}>{j2Nombre}</option>
              </select>
            </div>
            
            <div className="form-row checkbox">
              <label>
                <input 
                  type="checkbox"
                  checked={formEditar.forzar}
                  onChange={e => setFormEditar({...formEditar, forzar: e.target.checked})}
                />
                Actualizar avances en rondas siguientes
              </label>
            </div>
            
            <div className="form-actions">
              <button className="btn-cancel" onClick={cerrarModal}>Cancelar</button>
              <button 
                className="btn-save"
                onClick={() => handleEditarResultado(formEditar)}
                disabled={loading || !formEditar.ganadorId}
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal de asignar ganador - ahora es modal de resultado manual
  const renderModalAsignar = () => {
    if (!selectedPartida || modalType !== 'resultado') return null;
    
    const j1Nombre = selectedPartida.nombreJugador1 || selectedPartida.jugador1?.username || 'Jugador 1';
    const j2Nombre = selectedPartida.nombreJugador2 || selectedPartida.jugador2?.username || 'Jugador 2';
    
    const sets1 = parseInt(formResultado.sets1) || 0;
    const sets2 = parseInt(formResultado.sets2) || 0;
    const ganador = sets1 > sets2 ? j1Nombre : sets2 > sets1 ? j2Nombre : null;
    
    return (
      <div className="admin-modal-overlay" onClick={cerrarModal}>
        <div className="admin-modal" onClick={e => e.stopPropagation()}>
          <div className="admin-modal-header">
            <h3>🎯 Registrar Resultado</h3>
            <button className="modal-close" onClick={cerrarModal}>✕</button>
          </div>
          
          <p className="modal-description">
            Introduce el marcador de la partida. El jugador con más legs ganados avanzará de ronda automáticamente.
          </p>
          
          <div className="resultado-form">
            <div className="resultado-row">
              <span className="resultado-player">{j1Nombre}</span>
              <div className="resultado-input-group">
                <button 
                  className="resultado-btn minus"
                  onClick={() => setFormResultado({...formResultado, sets1: Math.max(0, sets1 - 1)})}
                >−</button>
                <input 
                  type="number" 
                  min="0" 
                  max="7"
                  value={formResultado.sets1}
                  onChange={e => setFormResultado({...formResultado, sets1: e.target.value})}
                  className="resultado-input"
                />
                <button 
                  className="resultado-btn plus"
                  onClick={() => setFormResultado({...formResultado, sets1: Math.min(7, sets1 + 1)})}
                >+</button>
              </div>
            </div>
            
            <div className="resultado-vs">VS</div>
            
            <div className="resultado-row">
              <span className="resultado-player">{j2Nombre}</span>
              <div className="resultado-input-group">
                <button 
                  className="resultado-btn minus"
                  onClick={() => setFormResultado({...formResultado, sets2: Math.max(0, sets2 - 1)})}
                >−</button>
                <input 
                  type="number" 
                  min="0" 
                  max="7"
                  value={formResultado.sets2}
                  onChange={e => setFormResultado({...formResultado, sets2: e.target.value})}
                  className="resultado-input"
                />
                <button 
                  className="resultado-btn plus"
                  onClick={() => setFormResultado({...formResultado, sets2: Math.min(7, sets2 + 1)})}
                >+</button>
              </div>
            </div>
            
            {ganador && (
              <div className="resultado-preview">
                🏆 <strong>{ganador}</strong> gana {Math.max(sets1, sets2)}-{Math.min(sets1, sets2)} y avanza
              </div>
            )}
            
            {sets1 === sets2 && sets1 > 0 && (
              <div className="resultado-error">
                ⚠️ No puede haber empate
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button className="btn-cancel" onClick={cerrarModal}>Cancelar</button>
            <button 
              className="btn-save"
              onClick={handleRegistrarResultado}
              disabled={loading || sets1 === sets2}
            >
              {loading ? 'Guardando...' : '✓ Registrar Resultado'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal de cambiar jugador
  const renderModalCambiar = () => {
    if (!selectedPartida || (modalType !== 'cambiar1' && modalType !== 'cambiar2')) return null;
    
    const posicion = modalType === 'cambiar1' ? 1 : 2;
    const jugadorActual = posicion === 1 
      ? (selectedPartida.nombreJugador1 || selectedPartida.jugador1?.username || 'TBD')
      : (selectedPartida.nombreJugador2 || selectedPartida.jugador2?.username || 'TBD');
    
    // Usar resultados de búsqueda en BD o mostrar participantes si no hay búsqueda
    const jugadoresAMostrar = searchTerm.length >= 2 
      ? searchResults 
      : [...participantes, ...jugadoresDisponibles];
    
    return (
      <div className="admin-modal-overlay" onClick={cerrarModal}>
        <div className="admin-modal large" onClick={e => e.stopPropagation()}>
          <div className="admin-modal-header">
            <h3>👤 Cambiar Jugador {posicion}</h3>
            <button className="modal-close" onClick={cerrarModal}>✕</button>
          </div>
          
          <p className="modal-description">
            Jugador actual: <strong>{jugadorActual}</strong>
          </p>
          
          <div className="player-search">
            <input 
              type="text"
              placeholder="Buscar usuario en la base de datos..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searching && <span className="search-indicator">🔍 Buscando...</span>}
          </div>
          
          <div className="players-list">
            {jugadoresAMostrar.slice(0, 20).map(jugador => (
              <button 
                key={jugador._id}
                className="player-option"
                onClick={() => handleCambiarJugador(posicion, jugador._id)}
                disabled={loading}
              >
                <span className="player-name">{jugador.username || jugador.nombre}</span>
                {participantes.find(p => p._id === jugador._id) && (
                  <span className="player-badge">Participante</span>
                )}
              </button>
            ))}
            
            {searchTerm.length >= 2 && !searching && jugadoresAMostrar.length === 0 && (
              <p className="no-results">No se encontraron usuarios</p>
            )}
            
            {searchTerm.length < 2 && jugadoresAMostrar.length === 0 && (
              <p className="no-results">Escribe al menos 2 caracteres para buscar</p>
            )}
          </div>
          
          <div className="bye-section">
            <button 
              className="btn-bye"
              onClick={() => handlePonerBye(posicion)}
              disabled={loading}
            >
              ⏭️ Poner BYE (el otro jugador avanza)
            </button>
          </div>
          
          <button className="btn-cancel full" onClick={cerrarModal}>Cancelar</button>
        </div>
      </div>
    );
  };

  // Ganador de la Gran Final (si completada) - DEBE estar ANTES del early return para el useEffect
  const granFinal = bracketData?.bracket?.granFinal;
  const ganadorGranFinalId = typeof granFinal?.ganador === 'string'
    ? granFinal?.ganador
    : granFinal?.ganador?._id;

  const ganadorGranFinalNombre = (() => {
    if (!granFinal) return null;
    if (granFinal?.ganador?.username || granFinal?.ganador?.nombre) {
      return granFinal.ganador.username || granFinal.ganador.nombre;
    }
    if (ganadorGranFinalId) {
      if (ganadorGranFinalId === (granFinal.jugador1?._id || granFinal.jugador1)) {
        return granFinal.nombreJugador1 || 'Ganador Winner';
      }
      if (ganadorGranFinalId === (granFinal.jugador2?._id || granFinal.jugador2)) {
        return granFinal.nombreJugador2 || 'Ganador Loser';
      }
    }
    return null;
  })();

  const hayGanadorFinal = granFinal?.completada && ganadorGranFinalId && ganadorGranFinalNombre;

  // Mostrar modal de campeón automáticamente cuando hay ganador
  useEffect(() => {
    if (hayGanadorFinal) {
      setShowCampeonModal(true);
    }
  }, [hayGanadorFinal]);

  // Estados vacíos
  if (!bracketData?.bracket) {
    return (
      <div className="bracket-empty">
        <p>El bracket aún no ha sido generado</p>
      </div>
    );
  }

  const tieneWinner = bracketData.bracket.winner?.rondas && 
    Object.keys(bracketData.bracket.winner.rondas).length > 0;
  const tieneLoser = bracketData.bracket.loser?.rondas && 
    Object.keys(bracketData.bracket.loser.rondas).length > 0;

  return (
    <div className="bracket-admin-container">
      {/* Banner de modo admin */}
      <div className="admin-banner">
        <span className="admin-icon">⚙️</span>
        <span>Modo Administrador - Click en cualquier partida para editar</span>
      </div>
      
      {/* Mensaje */}
      {mensaje.texto && (
        <div className={`admin-mensaje ${mensaje.tipo}`}>
          {mensaje.texto}
        </div>
      )}
      
      {/* Controles */}
      <div className="bracket-controls">
        {/* Solo mostrar toggle Winner/Loser en doble eliminación */}
        {!esEliminacionSimple && (
          <div className="bracket-toggle">
            <button 
              className={`toggle-btn ${modoVista === 'winner' ? 'active winner' : ''}`}
              onClick={() => setModoVista('winner')}
              disabled={!tieneWinner}
            >
              🏆 Winner
            </button>
            <button 
              className={`toggle-btn ${modoVista === 'loser' ? 'active loser' : ''}`}
              onClick={() => setModoVista('loser')}
              disabled={!tieneLoser}
            >
              ⚠️ Loser
            </button>
          </div>
        )}
        
        {/* Badge de eliminación simple */}
        {esEliminacionSimple && (
          <div className="bracket-badge-simple">
            ⚡ Eliminación Simple
          </div>
        )}
        
        {/* Buscador de jugadores */}
        <div className="bracket-search-container">
          <button 
            className="bracket-search-btn"
            onClick={() => setShowBuscador(!showBuscador)}
          >
            🔍 Buscar Jugador
          </button>
          
          {showBuscador && (
            <div className="bracket-search-dropdown">
              <input 
                type="text"
                placeholder="Escribe al menos 2 letras..."
                value={buscarJugador}
                onChange={e => setBuscarJugador(e.target.value)}
                autoFocus
                className="bracket-search-input"
              />
              <div className="bracket-search-results">
                {buscarJugador.length < 2 ? (
                  <span className="bracket-search-empty">🔍 Escribe para buscar...</span>
                ) : jugadoresFiltrados.length > 0 ? (
                  jugadoresFiltrados.slice(0, 10).map(jugador => (
                    <button 
                      key={jugador._id || jugador.usuario}
                      className="bracket-search-item"
                      onClick={() => buscarYScrollAJugador(
                        jugador.usuario || jugador._id, 
                        jugador.nombreUsuario || jugador.username
                      )}
                    >
                      {jugador.nombreUsuario || jugador.username}
                    </button>
                  ))
                ) : (
                  <span className="bracket-search-empty">No encontrado</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="bracket-info">
        <span className="bracket-tipo">
          {esEliminacionSimple 
            ? '⚡ Eliminación Simple' 
            : modoVista === 'winner' 
              ? '🏆 Winner Bracket' 
              : '⚠️ Loser Bracket'}
        </span>
        <span className="bracket-estado">
          Ronda {bracketData.bracket[modoVista]?.rondaActual || 1} de{' '}
          {bracketData.bracket[modoVista]?.totalRondas || 1}
        </span>
        <span className="bracket-formato">
          🎯 {getBracketFormatLabel({
            modoVista,
            tipoEliminacion: bracketData?.info?.tipoEliminacion,
            configuracion: bracketData?.configuracion,
          })}
        </span>
      </div>

      {/* Bracket scrollable */}
      <div className="bracket-scroll-container" ref={scrollContainerRef}>
        {renderBracket()}
      </div>

      {/* Leyenda */}
      <div className="bracket-leyenda">
        <span className="leyenda-item">
          <span className="leyenda-color ganador"></span> Ganador
        </span>
        <span className="leyenda-item">
          <span className="leyenda-color perdedor"></span> Eliminado
        </span>
        <span className="leyenda-item">
          <span className="leyenda-color pendiente"></span> Pendiente
        </span>
        <span className="leyenda-item">
          <span className="leyenda-color admin"></span> Click para editar
        </span>
      </div>
      
      {/* Modales */}
      {renderModalMenu()}
      {renderModalEditar()}
      {renderModalAsignar()}
      {renderModalCambiar()}

      {/* Modal de Campeón */}
      {showCampeonModal && hayGanadorFinal && (
        <div className="campeon-modal-overlay" onClick={() => setShowCampeonModal(false)}>
          <div className="campeon-modal" onClick={e => e.stopPropagation()}>
            <div className="campeon-modal-content">
              <div className="campeon-trophy">🏆</div>
              <h2 className="campeon-titulo">¡Tenemos Campeón!</h2>
              <div className="campeon-nombre">{ganadorGranFinalNombre}</div>
              <p className="campeon-subtitulo">ha ganado el torneo</p>
              {campeonatoNombre && (
                <p className="campeon-torneo">{campeonatoNombre}</p>
              )}
              <button className="campeon-cerrar-btn" onClick={() => setShowCampeonModal(false)}>
                Ver Bracket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketAdmin;
